import { ref, shallowRef } from 'vue'

const MIME_CANDIDATES = [
  'audio/ogg;codecs=opus',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
]

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') {
    return ''
  }

  return MIME_CANDIDATES.find(candidate => MediaRecorder.isTypeSupported(candidate)) || ''
}

export function isBradleyAudioFormatSupported() {
  return Boolean(pickMimeType())
}

function extensionForMimeType(mimeType) {
  if (mimeType.includes('ogg')) {
    return 'ogg'
  }

  if (mimeType.includes('mp4')) {
    return 'm4a'
  }

  return 'webm'
}

export function useBradleyAudioRecorder() {
  const isPaused = ref(false)
  const mediaRecorder = shallowRef(null)
  const chunks = shallowRef([])
  const startedAt = ref(null)
  const pausedAt = ref(null)
  const totalPauseMs = ref(0)

  function reset() {
    mediaRecorder.value = null
    chunks.value = []
    isPaused.value = false
    startedAt.value = null
    pausedAt.value = null
    totalPauseMs.value = 0
  }

  function startRecording(mediaStream, { chunkInterval = 1000 } = {}) {
    if (!mediaStream) {
      throw new Error('A media stream is required to start recording')
    }

    const mimeType = pickMimeType()
    if (!mimeType) {
      throw new Error('No supported audio format available in this browser')
    }

    const recorder = new MediaRecorder(mediaStream, { mimeType })

    chunks.value = []
    recorder.ondataavailable = (event) => {
      if (event.data?.size > 0) {
        chunks.value.push(event.data)
      }
    }

    mediaRecorder.value = recorder
    isPaused.value = false
    pausedAt.value = null
    totalPauseMs.value = 0
    startedAt.value = Date.now()

    recorder.start(chunkInterval)
  }

  function pauseRecording() {
    const recorder = mediaRecorder.value
    if (!recorder || isPaused.value) {
      return
    }

    isPaused.value = true
    pausedAt.value = Date.now()

    if (recorder.state === 'recording') {
      recorder.pause()
    }
  }

  function resumeRecording() {
    const recorder = mediaRecorder.value
    if (!recorder || !isPaused.value) {
      return
    }

    if (pausedAt.value) {
      totalPauseMs.value += Date.now() - pausedAt.value
      pausedAt.value = null
    }

    isPaused.value = false

    if (recorder.state === 'paused') {
      recorder.resume()
    }
  }

  function getRecordingDuration() {
    if (!startedAt.value) {
      return 0
    }

    const now = Date.now()
    let pauseMs = totalPauseMs.value

    if (isPaused.value && pausedAt.value) {
      pauseMs += now - pausedAt.value
    }

    return Math.max(0, now - startedAt.value - pauseMs)
  }

  function buildFile() {
    const recorder = mediaRecorder.value
    const mimeType = recorder?.mimeType || chunks.value[0]?.type || 'audio/webm'
    const blob = new Blob(chunks.value, { type: mimeType })
    const fileName = `bradley-audio-${Date.now()}.${extensionForMimeType(mimeType)}`

    return new File([blob], fileName, { type: mimeType })
  }

  function stopRecording() {
    const recorder = mediaRecorder.value

    if (!recorder) {
      return Promise.resolve(null)
    }

    return new Promise((resolve, reject) => {
      const settle = () => {
        try {
          const file = buildFile()
          reset()
          resolve(file)
        }
        catch (error) {
          reset()
          reject(error)
        }
      }

      recorder.onstop = settle
      recorder.onerror = (event) => {
        reset()
        reject(event?.error || new Error('Audio recording failed'))
      }

      if (recorder.state === 'inactive') {
        settle()
        return
      }

      recorder.stop()
    })
  }

  return {
    isPaused,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    getRecordingDuration,
  }
}
